'use client'

import { Icons } from '@/components/ui/icons'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import {
  ALL_PRODUCTS,
  ALL_SITES,
  type DashboardFilterContext,
  type DashboardFilterOption,
} from '@/lib/dashboard-scope-filters'

interface DashboardScopeFiltersProps {
  filterContext?: DashboardFilterContext | null
  products: DashboardFilterOption[]
  sites: DashboardFilterOption[]
  selectedProduct: string
  selectedSite: string
  onProductChange: (value: string) => void
  onSiteChange: (value: string) => void
}

export function DashboardScopeFilters({
  filterContext,
  products,
  sites,
  selectedProduct,
  selectedSite,
  onProductChange,
  onSiteChange,
}: DashboardScopeFiltersProps) {
  const showSite = filterContext?.show_site_filter ?? sites.length > 0
  const showProduct = filterContext?.show_product_filter ?? products.length > 0
  const siteLabel = filterContext?.site_label ?? 'View by site'
  const productLabel = filterContext?.product_label ?? 'View by product'

  if (!showSite && !showProduct) return null

  return (
    <div className='flex flex-col gap-4 border-t border-gray-100 pt-4'>
      {showSite && (
        <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
          <div className='flex items-center gap-2'>
            <Icons.building className='h-4 w-4 text-brand-cornflower' strokeWidth={1.5} />
            <span className='text-sm font-medium text-brand-navy'>{siteLabel}</span>
          </div>
          <Select value={selectedSite} onValueChange={onSiteChange}>
            <SelectTrigger className='w-full sm:w-64 bg-white text-brand-navy'>
              <SelectValue placeholder='All sites' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_SITES}>All sites (overview)</SelectItem>
              {sites.map((s) => (
                <SelectItem key={s.code} value={s.code}>
                  {s.label}
                  {s.call_count > 0 ? ` · ${s.call_count} calls` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {showProduct && (
        <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
          <div className='flex items-center gap-2'>
            <Icons.layers className='h-4 w-4 text-brand-cornflower' strokeWidth={1.5} />
            <span className='text-sm font-medium text-brand-navy'>{productLabel}</span>
          </div>
          <Select value={selectedProduct} onValueChange={onProductChange}>
            <SelectTrigger className='w-full sm:w-64 bg-white text-brand-navy'>
              <SelectValue placeholder='All products' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_PRODUCTS}>All products (overview)</SelectItem>
              {products.map((p) => (
                <SelectItem key={p.code} value={p.code}>
                  {p.label}
                  {p.call_count > 0 ? ` · ${p.call_count} calls` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {showProduct && products.length === 0 && (
        <p className='text-xs text-amber-600'>
          No products found in team mapping. Import team Excel with a Product column, then refresh.
        </p>
      )}
      {showSite && sites.length === 0 && (
        <p className='text-xs text-amber-600'>
          No sites found in team mapping. Import team Excel with Site columns, then refresh.
        </p>
      )}
    </div>
  )
}
